import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

function Trucks() {
  const nav = useNavigate();
  const [truckFinance, setTruckFinance] = useState({ deliveries: [], expenses: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  const [vehicles, setVehicles] = useState([]);
  const [banks, setBanks] = useState([]);
  const [truckProfits, setTruckProfits] = useState([]);
  const [showAddDelivery, setShowAddDelivery] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [newDelivery, setNewDelivery] = useState({
    vehicle_id: "",
    from: "",
    to: "",
    customer: "",
    bank: "",
    description: "",
    amount: "",
    date: ""
  });

  const [expenseItems, setExpenseItems] = useState([
    { supplier: "", bank: "", description: "", amount: "", date: "" }
  ]);
  const [selectedVehicleForExpenses, setSelectedVehicleForExpenses] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'fleetNo', direction: 'ascending' });

  const [groupByVehicle, setGroupByVehicle] = useState(true);

  const [printMode, setPrintMode] = useState(null);
  const [printVehicleId, setPrintVehicleId] = useState(null);
  const [printVehicleName, setPrintVehicleName] = useState("");

  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // const API_BASE = "http://localhost:8000";
  // const API_BASE ="http://192.168.100.9:8000 ";

  // const API_BASE = "https://www.fleetapi.exhibitsolutions.co.ke";
  const API_BASE = "https://abbebehauliers.fleetapi.exhibitsolutions.co.tz";
  

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const sortedTrucks = React.useMemo(() => {
    let sortableTrucks = [...truckProfits];
    if (sortConfig.key) {
      sortableTrucks.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'totalIncome' || sortConfig.key === 'totalExpenses' || sortConfig.key === 'netProfit') {
          aValue = parseFloat(aValue || 0);
          bValue = parseFloat(bValue || 0);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableTrucks;
  }, [truckProfits, sortConfig]);

  const currentTrucks = sortedTrucks.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedTrucks.length / itemsPerPage);

  const groupedExpenses = React.useMemo(() => {
    if (!groupByVehicle) {
      return { "All Expenses": truckFinance.expenses };
    }

    const grouped = {};
    truckFinance.expenses.forEach(expense => {
      const vehicleName = expense.vehicles?.plate_no || 'Unknown Vehicle';
      if (!grouped[vehicleName]) {
        grouped[vehicleName] = [];
      }
      grouped[vehicleName].push(expense);
    });

    return Object.keys(grouped)
      .sort()
      .reduce((acc, key) => {
        acc[key] = grouped[key];
        return acc;
      }, {});
  }, [truckFinance.expenses, groupByVehicle]);

  const groupedDeliveries = React.useMemo(() => {
    if (!groupByVehicle) {
      return { "All Deliveries": truckFinance.deliveries };
    }

    const grouped = {};
    truckFinance.deliveries.forEach(delivery => {
      const vehicleName = delivery.vehicles?.plate_no || 'Unknown Vehicle';
      if (!grouped[vehicleName]) {
        grouped[vehicleName] = [];
      }
      grouped[vehicleName].push(delivery);
    });

    return Object.keys(grouped)
      .sort()
      .reduce((acc, key) => {
        acc[key] = grouped[key];
        return acc;
      }, {});
  }, [truckFinance.deliveries, groupByVehicle]);

  const calculateGroupTotals = (items) => {
    return items.reduce((total, item) => total + parseFloat(item.amount || 0), 0);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const fetchTruckProfits = async (vehiclesList) => {
    try {
      const profitPromises = vehiclesList.map(async (vehicle) => {
        try {
          const res = await fetch(`${API_BASE}/truck-finance/summary/${vehicle.id}`);
          const data = await res.json();
          if (data.success) {
            return {
              truckId: vehicle.id,
              plateNo: vehicle.plate_no,
              fleetNo: vehicle.fleet_no,
              make: vehicle.make,
              ...data.truckFinance
            };
          }
        } catch (err) {
          console.error(`Error fetching profit for truck ${vehicle.id}:`, err);
        }
        return null;
      });

      const profits = (await Promise.all(profitPromises)).filter(p => p !== null);
      setTruckProfits(profits);
    } catch (error) {
      console.error("Error fetching truck profits:", error);
    }
  };

  const updateTruckProfit = async (vehicleId) => {
    try {
      const res = await fetch(`${API_BASE}/truck-finance/summary/${vehicleId}`);
      const data = await res.json();

      if (data.success) {
        setTruckProfits(prevProfits => {
          const updatedProfits = [...prevProfits];
          const truckIndex = updatedProfits.findIndex(t => t.truckId === vehicleId);

          if (truckIndex !== -1) {
            updatedProfits[truckIndex] = {
              ...updatedProfits[truckIndex],
              totalIncome: data.truckFinance.totalIncome,
              totalExpenses: data.truckFinance.totalExpenses,
              netProfit: data.truckFinance.netProfit
            };
          } else {
            const vehicle = vehicles.find(v => v.id === vehicleId);
            if (vehicle) {
              updatedProfits.push({
                truckId: vehicleId,
                plateNo: vehicle.plate_no,
                fleetNo: vehicle.fleet_no,
                make: vehicle.make,
                ...data.truckFinance
              });
            }
          }
          return updatedProfits;
        });
      }
    } catch (error) {
      console.error("Error updating truck profit:", error);
    }
  };

  const refreshAllData = async () => {
    try {
      const financeRes = await fetch(`${API_BASE}/truck-finance/all`);
      const financeData = await financeRes.json();
      if (financeData.success) setTruckFinance(financeData.truckFinance);

      if (vehicles.length > 0) {
        await fetchTruckProfits(vehicles);
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const banksRes = await fetch(`${API_BASE}/truck-finance/banks`);
        const banksData = await banksRes.json();
        if (banksData.success) setBanks(banksData.banks);

        const vehiclesRes = await fetch(`${API_BASE}/vehicles/all-vehicles`);
        const vehiclesData = await vehiclesRes.json();
        if (vehiclesData.docs) {
          setVehicles(vehiclesData.docs);
          fetchTruckProfits(vehiclesData.docs);
        }

        const financeRes = await fetch(`${API_BASE}/truck-finance/all`);
        if (!financeRes.ok) throw new Error("Failed to fetch truck finance data");
        const financeData = await financeRes.json();

        if (financeData.success && financeData.truckFinance) {
          setTruckFinance(financeData.truckFinance);
        } else {
          throw new Error(financeData.error || "Invalid response format");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshTrigger]);

const handleAddDelivery = async (e) => {
  e.preventDefault();

  if (!newDelivery.date) {
    showNotification("Please select a date for the delivery", "error");
    return;
  }

  try {
    // Format date properly for backend
    const formattedDelivery = {
      ...newDelivery,
      date: newDelivery.date + "T00:00:00.000Z" // Add time to make it full ISO string
    };

    const response = await fetch(`${API_BASE}/truck-finance/delivery`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formattedDelivery)
    });

    const data = await response.json();

    if (data.success) {
      await refreshAllData();
      setShowAddDelivery(false);
      setNewDelivery({
        vehicle_id: "",
        from: "",
        to: "",
        customer: "",
        bank: "",
        description: "",
        amount: "",
        date: ""
      });
      setRefreshTrigger(prev => !prev);
      showNotification("Delivery added successfully!", "success");
    } else {
      showNotification(`Error: ${data.error || "Failed to add delivery"}`, "error");
    }
  } catch (error) {
    console.error("Error adding delivery:", error);
    showNotification("Failed to add delivery. Check console for details.", "error");
  }
};

  const handleExpenseItemChange = (index, field, value) => {
    const updatedItems = [...expenseItems];
    updatedItems[index][field] = value;
    setExpenseItems(updatedItems);
  };

  const handleAddExpenseItem = () => {
    setExpenseItems([...expenseItems, { supplier: "", bank: "", description: "", amount: "", date: "" }]);
  };

  const handleRemoveExpenseItem = (index) => {
    if (expenseItems.length > 1) {
      const updatedItems = expenseItems.filter((_, i) => i !== index);
      setExpenseItems(updatedItems);
    }
  };

  const calculateTotalExpense = () => {
    return expenseItems.reduce((total, item) => {
      return total + (parseFloat(item.amount) || 0);
    }, 0);
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();

    if (!selectedVehicleForExpenses) {
      showNotification("Please select a vehicle", "error");
      return;
    }

    for (let i = 0; i < expenseItems.length; i++) {
      const item = expenseItems[i];
      if (!item.supplier || !item.bank || !item.description || !item.amount || !item.date) {
        showNotification(`Please fill all fields including date for expense item ${i + 1}`, "error");
        return;
      }
    }

    try {
      const promises = expenseItems.map(item =>
        fetch(`${API_BASE}/truck-finance/expense`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vehicle_id: selectedVehicleForExpenses,
            ...item
          })
        })
      );

      const responses = await Promise.all(promises);
      const results = await Promise.all(responses.map(res => res.json()));

      const allSuccess = results.every(result => result.success);

      if (allSuccess) {
        await refreshAllData();
        setRefreshTrigger(prev => !prev);

        showNotification(`Successfully added ${expenseItems.length} expense(s)! Total: KSh ${calculateTotalExpense().toLocaleString()}`, "success");

        setShowAddExpense(false);
        setSelectedVehicleForExpenses("");
        setExpenseItems([{ supplier: "", bank: "", description: "", amount: "", date: "" }]);
      } else {
        const errors = results.filter(r => !r.success).map(r => r.error).join(", ");
        showNotification(`Some expenses failed to add: ${errors}`, "error");
      }
    } catch (error) {
      console.error("Error adding expenses:", error);
      showNotification("Failed to add expenses. Check console for details.", "error");
    }
  };

  const handleCloseExpenseModal = () => {
    setShowAddExpense(false);
    setSelectedVehicleForExpenses("");
    setExpenseItems([{ supplier: "", bank: "", description: "", amount: "", date: "" }]);
  };

  const handleCloseDeliveryModal = () => {
    setShowAddDelivery(false);
    setNewDelivery({
      vehicle_id: "",
      from: "",
      to: "",
      customer: "",
      bank: "",
      description: "",
      amount: "",
      date: ""
    });
  };

  const handleDeleteDelivery = async (id) => {
    if (!window.confirm("Delete this delivery?")) return;

    try {
      const response = await fetch(`${API_BASE}/truck-finance/delivery/${id}`, {
        method: "DELETE",
      });

      const data = await response.json();
      if (data.success) {
        await refreshAllData();

        if (data.vehicle_id) {
          await updateTruckProfit(data.vehicle_id);
        }

        setRefreshTrigger(prev => !prev);

        showNotification("Delivery deleted successfully!", "success");
      } else {
        showNotification(`Error: ${data.error || "Failed to delete delivery"}`, "error");
      }
    } catch (error) {
      console.error("Error deleting delivery:", error);
      showNotification("Failed to delete delivery. Check console for details.", "error");
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;

    try {
      const response = await fetch(`${API_BASE}/truck-finance/expense/${id}`, {
        method: "DELETE",
      });

      const data = await response.json();
      if (data.success) {
        await refreshAllData();

        if (data.vehicle_id) {
          await updateTruckProfit(data.vehicle_id);
        }

        setRefreshTrigger(prev => !prev);

        showNotification("Expense deleted successfully!", "success");
      } else {
        showNotification(`Error: ${data.error || "Failed to delete expense"}`, "error");
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      showNotification("Failed to delete expense. Check console for details.", "error");
    }
  };

  const handlePrintAllIncome = () => {
    setPrintMode('all-income');
    setPrintVehicleId(null);
    setPrintVehicleName("All Trucks");
    setTimeout(() => {
      window.print();
      setPrintMode(null);
    }, 100);
  };

  const handlePrintAllExpenses = () => {
    setPrintMode('all-expenses');
    setPrintVehicleId(null);
    setPrintVehicleName("All Trucks");
    setTimeout(() => {
      window.print();
      setPrintMode(null);
    }, 100);
  };

  const handlePrintAllProfits = () => {
    setPrintMode('all-profits');
    setPrintVehicleId(null);
    setPrintVehicleName("All Trucks");
    setTimeout(() => {
      window.print();
      setPrintMode(null);
    }, 100);
  };

  const handlePrintSingleIncome = (vehicleName, vehicleId) => {
    setPrintMode('single-income');
    setPrintVehicleId(vehicleId);
    setPrintVehicleName(vehicleName);
    setTimeout(() => {
      window.print();
      setPrintMode(null);
    }, 100);
  };

  const handlePrintSingleExpense = (vehicleName, vehicleId) => {
    setPrintMode('single-expense');
    setPrintVehicleId(vehicleId);
    setPrintVehicleName(vehicleName);
    setTimeout(() => {
      window.print();
      setPrintMode(null);
    }, 100);
  };

  const handlePrintSingleProfit = (vehicleName, vehicleId) => {
    setPrintMode('single-profit');
    setPrintVehicleId(vehicleId);
    setPrintVehicleName(vehicleName);
    setTimeout(() => {
      window.print();
      setPrintMode(null);
    }, 100);
  };

  const calculateVehicleIncome = (vehicleId) => {
    return truckFinance.deliveries
      .filter(d => d.vehicle_id === vehicleId)
      .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
  };

  const calculateVehicleExpenses = (vehicleId) => {
    return truckFinance.expenses
      .filter(e => e.vehicle_id === vehicleId)
      .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  };

  const getTruckProfitData = (vehicleId) => {
    return truckProfits.find(truck => truck.truckId === vehicleId);
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        &laquo; Prev
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg border ${currentPage === i
            ? 'bg-blue-600 text-white border-blue-600'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
        >
          {i}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next &raquo;
      </button>
    );

    return buttons;
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const formatDateForPrint = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="p-8 bg-light_grey min-h-screen">
      {notification.show && (
        <div className="fixed top-4 right-4 z-[9999] animate-slide-in">
          <div className={`rounded-lg shadow-2xl p-4 min-w-[300px] max-w-md ${notification.type === 'success'
            ? 'bg-green-600 text-white'
            : 'bg-red-600 text-white'
            }`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification({ show: false, message: '', type: '' })}
                className="flex-shrink-0 text-white hover:text-gray-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white;
            padding: 20px;
          }
          .no-print {
            display: none !important;
          }
          .print-header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
          }
          .print-table th, .print-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            font-size: 11px;
          }
          .print-table th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .print-total {
            text-align: right;
            font-weight: bold;
            margin-top: 20px;
            padding-top: 10px;
            border-top: 2px solid #333;
          }
          .print-footer {
            text-align: center;
            margin-top: 30px;
            font-size: 10px;
            color: #666;
          }
        }
      `}</style>

      <div className="flex gap-4 mb-8 no-print">
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-orange-500 transition duration-200 font-semibold" onClick={() => nav("/dashboard/finance")}>Finance</button>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-orange-500 transition duration-200 font-semibold" onClick={() => nav("/dashboard/finance/statements")}>Statement</button>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-orange-500 transition duration-200 font-semibold" onClick={() => nav("/dashboard/finance/trucks")}>Truck/Trella</button>
      </div>

      <div className="flex gap-4 mb-6 no-print">
        <button onClick={() => setShowAddDelivery(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition duration-200 font-semibold flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Income
        </button>
        <button onClick={() => setShowAddExpense(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition duration-200 font-semibold flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Expense
        </button>
      </div>

      <h1 className="text-4xl font-bold mb-8 text-primary no-print">Trucks & Trella Dashboard</h1>

      {loading && (
        <div className="text-center py-8 no-print">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
          <p className="text-gray-600">Loading truck finance data...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 no-print">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">Error: {error}</p>
            </div>
          </div>
        </div>
      )}

      {printMode && (
        <div className="print-content">
          {printMode === 'all-income' && (
            <>
              <div className="print-header">
                <h1 className="text-2xl font-bold">ALL TRUCKS INCOME REPORT</h1>
                <p className="text-gray-600">Generated on: {new Date().toLocaleDateString()}</p>
                <p className="text-gray-600">Total Trucks: {truckProfits.length}</p>
              </div>

              <table className="print-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Plate No</th>
                    <th>Deliveries</th>
                    <th>Total Income (KSh)</th>
                  </tr>
                </thead>
                <tbody>
                  {truckProfits.map((truck) => {
                    const vehicleDeliveries = truckFinance.deliveries.filter(d => d.vehicle_id === truck.truckId);
                    const totalIncome = calculateVehicleIncome(truck.truckId);

                    return (
                      <tr key={truck.truckId}>
                        <td>{truck.fleetNo || truck.truckId}</td>
                        <td>{truck.plateNo}</td>
                        <td>{vehicleDeliveries.length}</td>
                        <td className="text-right font-semibold">{totalIncome.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="text-right font-bold">GRAND TOTAL:</td>
                    <td className="text-right font-bold text-green-600">
                      KSh {truckProfits.reduce((sum, truck) => sum + calculateVehicleIncome(truck.truckId), 0).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>

              <div className="print-footer">
                <p>End of Report - {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
              </div>
            </>
          )}

          {printMode === 'all-expenses' && (
            <>
              <div className="print-header">
                <h1 className="text-2xl font-bold">ALL TRUCKS EXPENSES REPORT</h1>
                <p className="text-gray-600">Generated on: {new Date().toLocaleDateString()}</p>
                <p className="text-gray-600">Total Trucks: {truckProfits.length}</p>
              </div>

              <table className="print-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Plate No</th>
                    <th>Expenses</th>
                    <th>Total Expenses (KSh)</th>
                  </tr>
                </thead>
                <tbody>
                  {truckProfits.map((truck) => {
                    const vehicleExpenses = truckFinance.expenses.filter(e => e.vehicle_id === truck.truckId);
                    const totalExpenses = calculateVehicleExpenses(truck.truckId);

                    return (
                      <tr key={truck.truckId}>
                        <td>{truck.fleetNo || truck.truckId}</td>
                        <td>{truck.plateNo}</td>
                        <td>{vehicleExpenses.length}</td>
                        <td className="text-right font-semibold text-red-600">{totalExpenses.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="text-right font-bold">GRAND TOTAL:</td>
                    <td className="text-right font-bold text-red-600">
                      KSh {truckProfits.reduce((sum, truck) => sum + calculateVehicleExpenses(truck.truckId), 0).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>

              <div className="print-footer">
                <p>End of Report - {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
              </div>
            </>
          )}

          {printMode === 'all-profits' && (
            <>
              <div className="print-header">
                <h1 className="text-2xl font-bold">ALL TRUCKS PROFIT/LOSS REPORT</h1>
                <p className="text-gray-600">Generated on: {new Date().toLocaleDateString()}</p>
                <p className="text-gray-600">Total Trucks: {truckProfits.length}</p>
              </div>

              <table className="print-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Plate No</th>
                    <th>Total Income</th>
                    <th>Total Expenses</th>
                    <th>Net Profit/Loss</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {truckProfits.map((truck) => {
                    const totalIncome = parseFloat(truck.totalIncome || 0);
                    const totalExpenses = parseFloat(truck.totalExpenses || 0);
                    const netProfit = totalIncome - totalExpenses;
                    const isProfit = netProfit >= 0;

                    return (
                      <tr key={truck.truckId}>
                        <td>{truck.fleetNo || truck.truckId}</td>
                        <td>{truck.plateNo}</td>
                        <td className="text-right">{totalIncome.toLocaleString()}</td>
                        <td className="text-right">{totalExpenses.toLocaleString()}</td>
                        <td className={`text-right font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                          {netProfit.toLocaleString()}
                        </td>
                        <td className={`text-center ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                          {isProfit ? 'PROFIT' : 'LOSS'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="2" className="text-right font-bold">TOTALS:</td>
                    <td className="text-right font-bold text-green-600">
                      KSh {truckProfits.reduce((sum, truck) => sum + parseFloat(truck.totalIncome || 0), 0).toLocaleString()}
                    </td>
                    <td className="text-right font-bold text-red-600">
                      KSh {truckProfits.reduce((sum, truck) => sum + parseFloat(truck.totalExpenses || 0), 0).toLocaleString()}
                    </td>
                    <td className="text-right font-bold">
                      KSh {truckProfits.reduce((sum, truck) => {
                        const totalIncome = parseFloat(truck.totalIncome || 0);
                        const totalExpenses = parseFloat(truck.totalExpenses || 0);
                        return sum + (totalIncome - totalExpenses);
                      }, 0).toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>

              <div className="print-footer">
                <p>End of Report - {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
              </div>
            </>
          )}

          {printMode === 'single-income' && printVehicleId && (
            <>
              <div className="print-header">
                <h1 className="text-2xl font-bold">{printVehicleName.toUpperCase()} - INCOME REPORT</h1>
                <p className="text-gray-600">Generated on: {new Date().toLocaleDateString()}</p>
              </div>

              {(() => {
                const vehicleDeliveries = truckFinance.deliveries.filter(d => d.vehicle_id === printVehicleId);
                const totalIncome = calculateVehicleIncome(printVehicleId);

                return (
                  <>
                    <table className="print-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Route</th>
                          <th>Customer</th>
                          <th>Bank</th>
                          <th>Description</th>
                          <th>Amount (KSh)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vehicleDeliveries.map((delivery) => (
                          <tr key={delivery.id}>
                            <td>{formatDateForPrint(delivery.date)}</td>
                            <td>{delivery.from} → {delivery.to}</td>
                            <td>{delivery.customer || '-'}</td>
                            <td>{delivery.bank || '-'}</td>
                            <td>{delivery.description || '-'}</td>
                            <td className="text-right">{parseFloat(delivery.amount).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="5" className="text-right font-bold">TOTAL INCOME:</td>
                          <td className="text-right font-bold text-green-600">
                            KSh {totalIncome.toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>

                    <div className="print-footer">
                      <p>End of Report - {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {printMode === 'single-expense' && printVehicleId && (
            <>
              <div className="print-header">
                <h1 className="text-2xl font-bold">{printVehicleName.toUpperCase()} - EXPENSES REPORT</h1>
                <p className="text-gray-600">Generated on: {new Date().toLocaleDateString()}</p>
              </div>

              {(() => {
                const vehicleExpenses = truckFinance.expenses.filter(e => e.vehicle_id === printVehicleId);
                const totalExpenses = calculateVehicleExpenses(printVehicleId);

                return (
                  <>
                    <table className="print-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Supplier</th>
                          <th>Bank</th>
                          <th>Description</th>
                          <th>Amount (KSh)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vehicleExpenses.map((expense) => (
                          <tr key={expense.id}>
                            <td>{formatDateForPrint(expense.date)}</td>
                            <td>{expense.supplier || '-'}</td>
                            <td>{expense.bank || '-'}</td>
                            <td>{expense.description || '-'}</td>
                            <td className="text-right text-red-600">{parseFloat(expense.amount).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="4" className="text-right font-bold">TOTAL EXPENSES:</td>
                          <td className="text-right font-bold text-red-600">
                            KSh {totalExpenses.toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>

                    <div className="print-footer">
                      <p>End of Report - {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {printMode === 'single-profit' && printVehicleId && (
            <>
              <div className="print-header">
                <h1 className="text-2xl font-bold">{printVehicleName.toUpperCase()} - PROFIT/LOSS REPORT</h1>
                <p className="text-gray-600">Generated on: {new Date().toLocaleDateString()}</p>
              </div>

              {(() => {
                const truckData = getTruckProfitData(printVehicleId);
                if (!truckData) return <p>No data found for this vehicle</p>;

                const totalIncome = parseFloat(truckData.totalIncome || 0);
                const totalExpenses = parseFloat(truckData.totalExpenses || 0);
                const netProfit = totalIncome - totalExpenses;
                const isProfit = netProfit >= 0;

                return (
                  <>
                    <div className="mb-6">
                      <table className="print-table">
                        <thead>
                          <tr>
                            <th>Vehicle Details</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="font-bold">Vehicle</td>
                            <td>{truckData.fleetNo || truckData.truckId}</td>
                          </tr>
                          <tr>
                            <td className="font-bold">Plate Number</td>
                            <td>{truckData.plateNo}</td>
                          </tr>
                          <tr>
                            <td className="font-bold">Make</td>
                            <td>{truckData.make || '-'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="mb-6">
                      <table className="print-table">
                        <thead>
                          <tr>
                            <th>Financial Summary</th>
                            <th>Amount (KSh)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="font-bold">Total Income</td>
                            <td className="text-right text-green-600">{totalIncome.toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td className="font-bold">Total Expenses</td>
                            <td className="text-right text-red-600">{totalExpenses.toLocaleString()}</td>
                          </tr>
                          <tr className={`${isProfit ? 'bg-green-50' : 'bg-red-50'}`}>
                            <td className="font-bold">Net Profit/Loss</td>
                            <td className={`text-right font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                              {netProfit.toLocaleString()} ({isProfit ? 'PROFIT' : 'LOSS'})
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="print-footer">
                      <p>End of Report - {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}

      {!loading && !error && !printMode && (
        <>
          {truckProfits.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-gray-800">Truck Profit/Loss Summary</h2>
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                  Showing {currentTrucks.length} of {truckProfits.length} trucks
                </span>
              </div>

              <div className="mb-4 flex justify-end">
                <button
                  onClick={handlePrintAllProfits}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 font-semibold flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print All Profits
                </button>
              </div>

              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                  <span className="text-sm text-gray-600">trucks per page</span>
                </div>

                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('fleetNo')}
                      >
                        Vehicles{getSortIndicator('fleetNo')}
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('plateNo')}
                      >
                        Plate No{getSortIndicator('plateNo')}
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('totalIncome')}
                      >
                        Total Revenue{getSortIndicator('totalIncome')}
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('totalExpenses')}
                      >
                        Total Expenses{getSortIndicator('totalExpenses')}
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('netProfit')}
                      >
                        Net Profit/Loss{getSortIndicator('netProfit')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentTrucks.map((truck) => {
                      let netProfit;

                      if (truck.netProfit !== undefined) {
                        netProfit = typeof truck.netProfit === 'string'
                          ? parseFloat(truck.netProfit)
                          : truck.netProfit;
                      } else {
                        const totalIncome = parseFloat(truck.totalIncome || 0);
                        const totalExpenses = parseFloat(truck.totalExpenses || 0);
                        netProfit = totalIncome - totalExpenses;
                      }

                      const isProfit = netProfit >= 0;
                      const formattedNetProfit = Math.abs(netProfit).toLocaleString();

                      return (
                        <tr key={truck.truckId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {truck.fleetNo || truck.truckId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {truck.plateNo}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                            KSh {parseInt(truck.totalIncome || 0).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                            KSh {parseInt(truck.totalExpenses || 0).toLocaleString()}
                          </td>
                          <td
                            style={{
                              color: isProfit ? '#059669' : '#dc2626',
                              backgroundColor: isProfit ? '#f0fdf4' : '#fef2f2'
                            }}
                            className="px-6 py-4 whitespace-nowrap text-sm font-bold rounded-lg"
                          >
                            {netProfit < 0 ? '- ' : ''}KSh {formattedNetProfit}
                            {!isProfit && netProfit < 0 && (
                              <span
                                style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
                                className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              >
                                Loss
                              </span>
                            )}
                            {isProfit && netProfit > 0 && (
                              <span
                                style={{ backgroundColor: '#dcfce7', color: '#166534' }}
                                className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              >
                                Profit
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handlePrintSingleProfit(truck.plateNo, truck.truckId)}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                                title="Print Profit/Loss"
                              >
                                Print
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedTrucks.length)} of {sortedTrucks.length} entries
                  </div>

                  <div className="flex gap-2">
                    {renderPaginationButtons()}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-6 mb-6 no-print">
            <div className="flex flex-wrap gap-4">
              <button
                onClick={handlePrintAllIncome}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 font-semibold flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print All Income
              </button>
              <button
                onClick={handlePrintAllExpenses}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 font-semibold flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print All Expenses
              </button>
              <button
                onClick={handlePrintAllProfits}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 font-semibold flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Income and Expenses
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Truck Income</h2>

              <div className="flex items-center gap-4">
                <span className="bg-blue-100 text-800 text-sm font-medium px-3 py-1 rounded-full">
                  {truckFinance.deliveries.length} records
                </span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={groupByVehicle}
                    onChange={(e) => setGroupByVehicle(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">Group by Vehicle</span>
                </label>
              </div>
            </div>

            {truckFinance.deliveries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="mt-2">No deliveries found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {Object.entries(groupedDeliveries).map(([vehicleName, deliveries]) => {
                  const vehicle = vehicles.find(v => v.plate_no === vehicleName);
                  const vehicleId = vehicle?.id;

                  return (
                    <div key={vehicleName} className="mb-8 last:mb-0">
                      <div className="flex justify-between items-center mb-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded-lg">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-gray-800">{vehicleName}</h3>
                            <p className="text-sm text-gray-600">{deliveries.length} delivery(ies)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Total Income:</p>
                            <p className="text-lg font-bold text-green-600">
                              KSh {calculateGroupTotals(deliveries).toLocaleString()}
                            </p>
                          </div>
                          {vehicleId && (
                            <button
                              onClick={() => handlePrintSingleIncome(vehicleName, vehicleId)}
                              className="ml-4 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                              title="Print Income Report"
                            >
                              Print
                            </button>
                          )}
                        </div>
                      </div>

                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From → To</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {deliveries.map((d) => (
                            <tr key={d.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-500">{d.from} → {d.to}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{d.customer || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{d.bank || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{d.description}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-green-600">KSh {parseFloat(d.amount).toLocaleString()}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{new Date(d.date).toLocaleDateString()}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                <button
                                  onClick={() => handleDeleteDelivery(d.id)}
                                  className="bg-red-600 text-red px-4 py-2 rounded hover:bg-red-700 transition duration-200"
                                  title="Delete delivery"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6 inline-block"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Truck Expenses</h2>
              <div className="flex items-center gap-4">
                <span className="bg-red-100 text-red-800 text-sm font-medium px-3 py-1 rounded-full">
                  {truckFinance.expenses.length} records
                </span>c
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={groupByVehicle}
                    onChange={(e) => setGroupByVehicle(e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-600">Group by Vehicle</span>
                </label>
              </div>
            </div>

            {truckFinance.expenses.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="mt-2">No expenses found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {Object.entries(groupedExpenses).map(([vehicleName, expenses]) => {
                  const vehicle = vehicles.find(v => v.plate_no === vehicleName);
                  const vehicleId = vehicle?.id;

                  return (
                    <div key={vehicleName} className="mb-8 last:mb-0">
                      <div className="flex justify-between items-center mb-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="bg-red-100 p-2 rounded-lg">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-gray-800">{vehicleName}</h3>
                            <p className="text-sm text-gray-600">{expenses.length} expense(s)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Total Expenses:</p>
                            <p className="text-lg font-bold text-red-600">
                              KSh {calculateGroupTotals(expenses).toLocaleString()}
                            </p>
                          </div>
                          {vehicleId && (
                            <button
                              onClick={() => handlePrintSingleExpense(vehicleName, vehicleId)}
                              className="ml-4 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                              title="Print Expense Report"
                            >
                              Print
                            </button>
                          )}
                        </div>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {expenses.map((e) => (
                            <tr key={e.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{e.supplier || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{e.bank || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{e.description}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-red-600">KSh {parseFloat(e.amount).toLocaleString()}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{new Date(e.date).toLocaleDateString()}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                <button
                                  onClick={() => handleDeleteExpense(e.id)}
                                  className="bg-red-600 text-red px-4 py-2 rounded hover:bg-red-700 transition duration-200"
                                  title="Delete expense"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6 inline-block"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {showAddDelivery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-bold text-gray-800">Add Income</h3>
              <button onClick={handleCloseDeliveryModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddDelivery} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle *</label>
                <select required value={newDelivery.vehicle_id} onChange={(e) => setNewDelivery({ ...newDelivery, vehicle_id: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Select Vehicle</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.plate_no} - {v.make} ({v.fleet_no})</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                <input
                  type="date"
                  required
                  value={newDelivery.date}
                  onChange={(e) => setNewDelivery({ ...newDelivery, date: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">From *</label>
                <input type="text" required placeholder="e.g., Nairobi" value={newDelivery.from} onChange={(e) => setNewDelivery({ ...newDelivery, from: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">To *</label>
                <input type="text" required placeholder="e.g., Mombasa" value={newDelivery.to} onChange={(e) => setNewDelivery({ ...newDelivery, to: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
                <input type="text" required placeholder="Customer name" value={newDelivery.customer} onChange={(e) => setNewDelivery({ ...newDelivery, customer: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank *</label>
                <select required value={newDelivery.bank} onChange={(e) => setNewDelivery({ ...newDelivery, bank: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Select Bank</option>
                  {banks.map(bank => (
                    <option key={bank} value={bank}>{bank}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input type="text" placeholder="Additional details" value={newDelivery.description} onChange={(e) => setNewDelivery({ ...newDelivery, description: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (KSh) *</label>
                <input type="number" required min="0" step="0.01" placeholder="15000" value={newDelivery.amount} onChange={(e) => setNewDelivery({ ...newDelivery, amount: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-blue py-3 rounded-lg hover:bg-blue-700 font-semibold"
                >
                  Save Income
                </button>
                <button
                  type="button"
                  onClick={handleCloseDeliveryModal}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Add Multiple Expenses</h3>
                <p className="text-sm text-gray-600 mt-1">Add multiple expense items for the same vehicle</p>
              </div>
              <button onClick={handleCloseExpenseModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddExpense} className="p-6">
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="font-bold">Vehicle for all expenses *</span>
                </label>
                <select
                  required
                  value={selectedVehicleForExpenses}
                  onChange={(e) => setSelectedVehicleForExpenses(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Vehicle</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.plate_no} - {v.make} ({v.fleet_no})</option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-2">This vehicle will be used for all expense items below</p>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-700">Expense Items</h4>
                  <button
                    type="button"
                    onClick={handleAddExpenseItem}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 font-semibold flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Expense Item
                  </button>
                </div>

                {expenseItems.map((item, index) => (
                  <div key={index} className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex justify-between items-center mb-3">
                      <h5 className="font-medium text-gray-700">Expense Item {index + 1}</h5>
                      {expenseItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveExpenseItem(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                        <input
                          type="date"
                          required
                          value={item.date}
                          onChange={(e) => handleExpenseItemChange(index, 'date', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                        <input
                          type="text"
                          required
                          placeholder="Supplier name"
                          value={item.supplier}
                          onChange={(e) => handleExpenseItemChange(index, 'supplier', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bank *</label>
                        <select
                          required
                          value={item.bank}
                          onChange={(e) => handleExpenseItemChange(index, 'bank', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Bank</option>
                          {banks.map(bank => (
                            <option key={bank} value={bank}>{bank}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KSh) *</label>
                        <input
                          type="number"
                          required
                          min="0"
                          step="0.01"
                          placeholder="5000"
                          value={item.amount}
                          onChange={(e) => handleExpenseItemChange(index, 'amount', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g., Diesel purchase at Shell"
                          value={item.description}
                          onChange={(e) => handleExpenseItemChange(index, 'description', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {expenseItems.length > 0 && (
                <div className="mb-6 p-4 bg-green-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Total Expense Items:<span className="font-bold">{expenseItems.length}</span></p>
                      <p className="text-sm text-gray-600">All items will be added for the selected vehicle</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-700">Total Amount:</p>
                      <p className="text-2xl font-bold text-green-600">KSh {calculateTotalExpense().toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold"
                >
                  Add All Expenses ({expenseItems.length})
                </button>
                <button
                  type="button"
                  onClick={handleCloseExpenseModal}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 font-semibold"
                >
                   Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Trucks;